import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { useParams, useNavigate } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import MasrafGrubuSecici from '@/components/insaat/MasrafGrubuSecici';

const statusLabels = { submitted: 'Gönderildi', approved: 'Onaylandı', rejected: 'Reddedildi' };
const PARA_BIRIMLERI = ['TRY', 'GBP', 'EUR', 'USD'];
const para = (v) => (v == null ? '' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Para birimi başına toplam ("12.500,00 TRY · 300,00 GBP"). Eski raporlarda
// yalnız `tutanak_toplam` var.
export const toplamMetni = (r) => {
  const t = r?.tutanak_toplamlari;
  if (t && Object.keys(t).length) return Object.entries(t).map(([pb, v]) => `${para(v)} ${pb}`).join(' · ');
  return r?.tutanak_toplam != null ? para(r.tutanak_toplam) : '—';
};
const statusColors = {
  submitted: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  approved: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  rejected: 'bg-[#7a3535]/10 text-[#7a3535]',
};

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/insaat/reports');
  const [report, setReport] = useState(null);
  const [siteName, setSiteName] = useState('-');
  const [subName, setSubName] = useState('-');
  const [ustAdi, setUstAdi] = useState(null);   // rapor bir alt taşeronunsa
  const [showReject, setShowReject] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  // Ofisin satır fiyatı girişi (ilave işin fiyatını alt yüklenici vermez)
  const [fiyatDuzen, setFiyatDuzen] = useState(null);   // {line_id, birim_fiyat, para_birimi}
  // İlave işin masraf grubu: alt yüklenicinin seçimi öneri, son söz ofiste
  const [masrafGruplari, setMasrafGruplari] = useState([]);

  useEffect(() => { fetchReport(); }, [id]);

  const fetchReport = async () => {
    try {
      const res = await api.get(`/insaat/reports/${id}`);
      setReport(res.data);
      const [siteRes, subRes] = await Promise.all([
        api.get('/insaat/sites'), api.get('/insaat/subcontractors'),
      ]);
      setSiteName(siteRes.data.find((s) => s.id === res.data.site_id)?.name || '-');
      if ((res.data.tutanak_items || []).some((x) => !x.kalem_id)) {
        api.get('/insaat/masraf-gruplari').then((m) => setMasrafGruplari(m.data || [])).catch(() => {});
      }
      const sub = subRes.data.find((s) => s.id === res.data.subcontractor_id);
      setSubName(sub?.name || '-');
      setUstAdi(sub?.parent_id ? subRes.data.find((s) => s.id === sub.parent_id)?.name || null : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rapor yüklenemedi');
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await api.post(`/insaat/reports/${id}/approve`);
      toast.success('Rapor onaylandı');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!rejectNote.trim()) return;
    setActionLoading(true);
    try {
      await api.post(`/insaat/reports/${id}/reject`, { review_note: rejectNote });
      toast.success('Rapor reddedildi — alt yüklenici linkten tekrar düzenleyebilir');
      setShowReject(false);
      setRejectNote('');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setActionLoading(false);
  };

  const fiyatKaydet = async () => {
    setActionLoading(true);
    try {
      const r = await api.post(`/insaat/reports/${id}/satir-fiyat`, fiyatDuzen);
      setReport(r.data);
      setFiyatDuzen(null);
      toast.success('Fiyat kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    }
    setActionLoading(false);
  };

  const masrafKaydet = async (lineId, masrafId) => {
    try {
      const r = await api.post(`/insaat/reports/${id}/satir-fiyat`, { line_id: lineId, masraf_grubu_id: masrafId });
      setReport(r.data);
      toast.success('Masraf grubu kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  if (!report) return null;

  // İş takibi: satır çok günlük bir işin parçasıysa o işin özeti
  const isDurumu = (it) => {
    const o = it.is_id && (report.is_ozetleri || {})[it.is_id];
    if (!o || (o.gunler.length <= 1 && it.tamamlandi)) return null;   // tek günde biten iş: rozet gereksiz
    const gunNo = o.gunler.findIndex((g) => g.line_id === it.line_id) + 1;
    return (
      <div className="mt-1 text-xs text-[#24557a]" data-testid={`is-durumu-${it.line_id}`}>
        <span className={`status-badge ${it.tamamlandi ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#2e6fa0]/10 text-[#24557a]'}`}>
          {it.tamamlandi ? 'Tamamlandı' : 'Devam ediyor'} · {gunNo}. gün
        </span>
        <span className="ml-1.5 text-[#8a7e70]">
          {o.baslangic}{o.bitis ? ` → ${o.bitis}` : ''} · toplam {o.adam_gun} adam·gün, {o.adam_saat} adam·saat
        </span>
      </div>
    );
  };

  // "Yapan" sütunu yalnız bir satır alt taşeron ADINA girilmişse; yoksa boş
  // bir sütun her raporda gürültü olurdu.
  const yapanVar = [...(report.tutanak_items || []), ...(report.puantaj_entries || [])]
    .some((x) => x.alt_taseron_adi);
  const yapan = (x) => x.alt_taseron_adi || subName;

  return (
    <div data-testid="insaat-report-detail-page">
      <button onClick={geri} className="flex items-center gap-1 text-sm text-[#8a7e70] hover:text-[#1a1916] mb-4">
        <ArrowLeft size={14} /> Raporlara Dön
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916] tracking-tight">{subName} — {siteName}</h1>
          <p className="text-sm text-[#8a7e70]">
            {report.report_date}{ustAdi ? ` · ${ustAdi} alt taşeronu` : ''}
          </p>
        </div>
        <div className="flex gap-1">
          {report.calisma_yok && <span className="status-badge bg-[#e8dfd0] text-[#5c554b]">Çalışma yok</span>}
          <span className={`status-badge ${statusColors[report.status] || ''}`}>{statusLabels[report.status] || report.status}</span>
        </div>
      </div>

      {report.status === 'rejected' && report.review_note && (
        <div className="mb-4 p-3 rounded-lg bg-[#7a3535]/5 border border-[#7a3535]/30 text-sm text-[#7a3535]">
          Red gerekçesi: {report.review_note}
        </div>
      )}

      <h2 className="text-lg font-bold text-[#1a1916] mb-2">Tutanak</h2>
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden mb-6">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Poz No</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">İş</TableHead>
              {yapanVar && <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Yapan</TableHead>}
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Konum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ekip</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Birim Fiyat</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Açıklama</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(report.tutanak_items || []).map((it) => (
              <TableRow key={it.line_id} className="border-[#d0c4b0]">
                <TableCell className="font-mono text-sm text-[#3F3E39]">{it.poz_no || '—'}</TableCell>
                <TableCell className="text-[#1a1916]">
                  {it.is_cinsi}
                  {!it.kalem_id && <span className="ml-1.5 status-badge bg-[#7a6a2e]/10 text-[#7a6a2e]">İlave</span>}
                  {isDurumu(it)}
                  {!it.kalem_id && (
                    <div className="mt-1.5 max-w-xs" data-testid={`masraf-${it.line_id}`}>
                      {report.status !== 'approved' && masrafGruplari.length > 0 ? (
                        <MasrafGrubuSecici gruplar={masrafGruplari} value={it.masraf_grubu_id} kodlu
                          baslik="Masraf grubunu seçin" hatali={!it.masraf_grubu_id}
                          onChange={(v) => masrafKaydet(it.line_id, v)} testId={`masraf-sec-${it.line_id}`} />
                      ) : (
                        <span className="text-xs text-[#8a7e70]">
                          {it.masraf_grubu_kod && <span className="font-mono mr-1">{it.masraf_grubu_kod}</span>}
                          {it.masraf_grubu_yolu || 'Masraf grubu yok'}
                        </span>
                      )}
                      {it.masraf_ofis && <span className="block text-[11px] text-[#8a7e70] mt-0.5">Ofis düzeltti: {it.masraf_ofis.kim}</span>}
                    </div>
                  )}
                </TableCell>
                {yapanVar && <TableCell className={`text-sm ${it.alt_taseron_adi ? 'text-[#2e6fa0] font-medium' : 'text-[#8a7e70]'}`}>{yapan(it)}</TableCell>}
                <TableCell className="text-sm text-[#3F3E39]">{it.konum_yolu || '—'}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">
                  {(it.ekip || []).length ? it.ekip.map(e => `${e.adet} ${e.pozisyon}`).join(', ') : '—'}
                  {(it.ekip || []).length > 0 && it.saat && <span className="block text-xs text-[#8a7e70]">{it.saat} saat</span>}
                </TableCell>
                <TableCell className="text-[#8a7e70]">{it.birim}</TableCell>
                <TableCell className="text-right font-mono">{it.miktar}</TableCell>
                <TableCell className="text-right font-mono whitespace-nowrap">
                  {fiyatDuzen?.line_id === it.line_id ? (
                    <span className="inline-flex items-center gap-1">
                      <Input type="number" step="any" min="0" value={fiyatDuzen.birim_fiyat} autoFocus
                        onChange={(e) => setFiyatDuzen(p => ({ ...p, birim_fiyat: e.target.value }))}
                        className="h-8 w-24 bg-white border-[#d0c4b0] text-right" data-testid={`satir-fiyat-${it.line_id}`} />
                      <select value={fiyatDuzen.para_birimi} onChange={(e) => setFiyatDuzen(p => ({ ...p, para_birimi: e.target.value }))}
                        className="h-8 rounded-md border border-[#d0c4b0] bg-white px-1 text-xs">
                        {PARA_BIRIMLERI.map(pb => <option key={pb}>{pb}</option>)}
                      </select>
                      <Button size="sm" className="h-8 bg-[#3d6e4e] text-white" disabled={actionLoading} onClick={fiyatKaydet}>✓</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setFiyatDuzen(null)}>✕</Button>
                    </span>
                  ) : (
                    <button type="button" title="Fiyatı değiştir" data-testid={`fiyat-ver-${it.line_id}`}
                      onClick={() => setFiyatDuzen({ line_id: it.line_id, birim_fiyat: it.birim_fiyat ?? '', para_birimi: it.para_birimi || 'TRY' })}
                      className={it.birim_fiyat == null ? 'text-xs font-sans font-semibold text-[#9b3b2f] underline' : 'hover:underline'}>
                      {it.birim_fiyat == null ? 'Fiyat ver' : `${para(it.birim_fiyat)} ${it.para_birimi || ''}`}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold whitespace-nowrap">
                  {it.tutar == null ? '—' : `${para(it.tutar)} ${it.para_birimi || ''}`}
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{it.aciklama}</TableCell>
              </TableRow>
            ))}
            {(report.tutanak_items || []).length === 0 && (
              <TableRow><TableCell colSpan={yapanVar ? 10 : 9} className="text-center py-6 text-[#8a7e70]">Kalem yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex justify-end p-3 border-t border-[#d0c4b0] bg-[#e8dfd0]/50">
          <p className="font-bold text-[#1a1916]">
            Toplam: {toplamMetni(report)}
            {(report.tutanak_items || []).some(x => x.birim_fiyat == null) && (
              <span className="ml-2 text-xs font-normal text-[#9b3b2f]">(fiyatsız ilave iş var)</span>
            )}
          </p>
        </div>
      </div>

      <h2 className="text-lg font-bold text-[#1a1916] mb-2">Puantaj</h2>
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden mb-6">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Pozisyon</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Adet</TableHead>
              {yapanVar && <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kimin İşçisi</TableHead>}
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Konum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Yapılan İş</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(report.puantaj_entries || []).map((p) => (
              <TableRow key={p.entry_id} className="border-[#d0c4b0]">
                <TableCell className="text-[#1a1916] font-medium">{p.pozisyon}</TableCell>
                <TableCell className="text-right font-mono">{p.adet}</TableCell>
                {yapanVar && <TableCell className={`text-sm ${p.alt_taseron_adi ? 'text-[#2e6fa0] font-medium' : 'text-[#8a7e70]'}`}>{yapan(p)}</TableCell>}
                <TableCell className="text-sm text-[#3F3E39]">{p.konum_yolu || '—'}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{p.yapilan_is}</TableCell>
              </TableRow>
            ))}
            {(report.puantaj_entries || []).length === 0 && (
              <TableRow><TableCell colSpan={yapanVar ? 5 : 4} className="text-center py-6 text-[#8a7e70]">Puantaj kaydı yok</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {report.status !== 'approved' && (
        <div className="flex items-center gap-2">
          <Button onClick={handleApprove} disabled={actionLoading} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="approve-report-btn">
            <CheckCircle size={14} className="mr-1" /> Onayla
          </Button>
          <Button variant="outline" onClick={() => setShowReject(true)} className="border-[#7a3535] text-[#7a3535] hover:bg-[#7a3535]/10" data-testid="reject-report-btn">
            <XCircle size={14} className="mr-1" /> Reddet
          </Button>
        </div>
      )}

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <XCircle size={18} className="text-[#7a3535]" /> Raporu Reddet
            </DialogTitle>
            <DialogDescription className="text-[#8a7e70] text-sm">
              Red gerekçesi alt yükleniciye bağlantı üzerinden gösterilir ve rapor tekrar düzenlenebilir hale gelir.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Red nedeninizi yazın..." className="bg-white border-[#d0c4b0] min-h-[80px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleReject} disabled={actionLoading || !rejectNote.trim()} className="bg-[#7a3535] text-white hover:bg-[#5a2525]">
              {actionLoading ? 'İşleniyor...' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
